// communication.js

/**
 * @param {string} url
 * @param {Object} [options={}]
 * @returns {Promise<any>}
 */
async function apiRequest(url, options = {}) {
  try {
    console.log(`Making API request to: ${url}`);
    const response = await fetch(url, options);

    // for testing ngrok browser warning || local development
    // const response = await fetch(url, { ...options, headers: { ...(options?.headers), 'ngrok-skip-browser-warning': 'test' } });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
}

// utils.js

const EXPIRY = 1 * 60 * 60 * 1000; // 1 hour in milliseconds

function setWithExpiry(key, value, ttl) {
  const now = new Date();
  const item = {
    value: value,
    expiry: now.getTime() + ttl,
  };
  localStorage.setItem(key, JSON.stringify(item));
}

function getWithExpiry(key) {
  const itemStr = localStorage.getItem(key);
  if (!itemStr) {
    return null;
  }
  const item = JSON.parse(itemStr);
  const now = new Date();
  if (now.getTime() > item.expiry) {
    localStorage.removeItem(key);
    return null;
  }
  return item.value;
}

// communication.js

export class CommunicationManager {
  constructor(config = {}) {
    this.tenant = null;
    this.theme = null;
    this.ip_hash = null;
    this.token = config.token;
    this.session_id = null;
    this.ws = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectTimeout = null;
    this.connectionState = 'disconnected'; // 'connected', 'connecting', 'disconnected', 'failed'

    this.onMessage = null;
    this.onConnect = null;
    this.onDisconnect = null;
    this.onError = null;

    this.API_BASE = config.api_base;
    console.log("API_BASE", this.API_BASE);

    this.API_ROUTES = {
      GET_TENANT: `${this.API_BASE}/api/tenants`,
      GET_THEME: `${this.API_BASE}/api/themes`,
      WEBSOCKET: `${this.API_BASE}/api/ws`,
    };
    console.log("API_ROUTES", this.API_ROUTES);
  }

  async initialize() {
    try {
      const savedSessionId = localStorage.getItem("sessionId");
      if (savedSessionId) {
        console.log("Found saved session ID:", savedSessionId);
        this.session_id = savedSessionId;
      } else {
        console.log("No saved session ID found, starting new session");
      }
      await this.getTenant();
      await this.getTheme();
      await this.getIpHash();
      await this.getDeviceInfo();
    } catch (error) {
      console.error("Initialization error:", error);
      // Clear potentially corrupted session data
      this.clearSessionData();
    }

    // Listen for page refresh or close
    window.addEventListener("beforeunload", (e) => {
      console.log("Page is being refreshed or closed");
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.disconnect();
      }
    });

    window.addEventListener("unload", () => {
      this.closeWebSocketSession();
    });
  }

  clearSessionData() {
    console.log("Clearing session data due to errors");
    localStorage.removeItem("sessionId");
    localStorage.removeItem("tenant");
    localStorage.removeItem("theme");
    localStorage.removeItem("messages");
    this.session_id = null;
    this.tenant = null;
    this.theme = null;
  }

  async getTenant() {
    const tenants = await apiRequest(
      this.API_ROUTES.GET_TENANT + "?token=" + this.token
    );
    if (tenants.total === 0) {
      throw new Error("No tenant found with token: " + this.token);
    }
    this.tenant = tenants.items[0];

    return this.tenant;
  }

  async getTheme() {
    try {
      const cachedTheme = getWithExpiry("theme");
      if (cachedTheme) {
        this.theme = cachedTheme;
        console.log("Theme", cachedTheme, "found in local storage");
        return this.theme;
      }

      const themes = await apiRequest(
        this.API_ROUTES.GET_THEME + "?tenant_id=" + this.tenant.id
      );
      if (themes.total === 0) {
        throw new Error("No theme found for tenant: " + this.tenant.id);
      }
      this.theme = themes.items[0];

      setWithExpiry("theme", this.theme, EXPIRY);
      return this.theme;
    } catch (error) {
      console.error("Error fetching theme:", error);
      return null;
    }
  }

  async getIpHash() {
    const cachedIpHash = getWithExpiry("ip_hash");
    if (cachedIpHash) {
      this.ip_hash = cachedIpHash;
      console.log("IP Hash", cachedIpHash, "found in local storage");
      return this.ip_hash;
    }

    try {
      const data = await apiRequest("https://api.ipify.org?format=json");
      this.ipHash = this.hashString(data.ip);
      setWithExpiry("ip_hash", this.ipHash, EXPIRY);
      return this.ipHash;
    } catch (error) {
      console.error("Error fetching IP:", error);
      return null;
    }
  }

  hashString(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return hash.toString(16);
  }

  getDeviceInfo() {
    const cachedDeviceInfo = getWithExpiry("device_info");
    if (cachedDeviceInfo) {
      console.log("DeviceInfo", cachedDeviceInfo, "found in local storage");
      return cachedDeviceInfo;
    }

    let os = "unknown";
    let device = "unknown";
    let browser = "unknown";

    if (navigator.userAgentData) {
      os = navigator.userAgentData.platform;
      device = navigator.userAgentData.mobile ? "mobile" : "desktop";
    } else {
      const userAgent = navigator.userAgent.toLowerCase();
      if (userAgent.includes("win")) os = "Windows";
      else if (userAgent.includes("mac")) os = "MacOS";
      else if (userAgent.includes("linux")) os = "Linux";
      else if (userAgent.includes("android")) os = "Android";
      else if (userAgent.includes("ios")) os = "iOS";

      if (userAgent.includes("mobile")) device = "mobile";
      else if (userAgent.includes("tablet")) device = "tablet";
      else device = "desktop";
    }

    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.indexOf("firefox") > -1) {
      browser = "Firefox";
    } else if (userAgent.indexOf("chrome") > -1) {
      browser = "Chrome";
    } else if (userAgent.indexOf("safari") > -1) {
      browser = "Safari";
    } else if (
      userAgent.indexOf("opera") > -1 ||
      userAgent.indexOf("opr") > -1
    ) {
      browser = "Opera";
    } else if (userAgent.indexOf("edge") > -1) {
      browser = "Edge";
    } else if (userAgent.indexOf("trident") > -1) {
      browser = "Internet Explorer";
    }

    let deviceInfo = {
      browser: browser,
      agent: navigator.userAgent,
      os: os,
      device: device,
    };
    setWithExpiry("device_info", deviceInfo, EXPIRY);
    return deviceInfo;
  }

  connectWebSocket(sessionId = this.session_id) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log("WebSocket is already connected");
      return;
    }

    if (this.connectionState === 'connecting') {
      console.log("WebSocket connection already in progress");
      return;
    }

    this.connectionState = 'connecting';

    // Clear any existing reconnect timeout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    let url;
    if (
      this.API_ROUTES.WEBSOCKET.startsWith("http://") ||
      this.API_ROUTES.WEBSOCKET.startsWith("https://")
    ) {
      url = new URL(this.API_ROUTES.WEBSOCKET);
    } else {
      url = new URL(this.API_ROUTES.WEBSOCKET, window.location.origin);
    }
    url.protocol = url.protocol.replace("http", "ws");

    // Add query parameters
    let device_info = this.getDeviceInfo();
    url.searchParams.append("token", this.token);
    url.searchParams.append("browser", device_info.browser);
    url.searchParams.append("agent", device_info.agent);
    url.searchParams.append("os", device_info.os);
    url.searchParams.append("device", device_info.device);
    url.searchParams.append("ip_hash", this.ip_hash);

    // Use the saved session ID if available
    if (sessionId) {
      url.searchParams.append("session_id", sessionId);
      console.log("Attempting to reuse session ID:", sessionId);
    }

    this.ws = new WebSocket(url.toString());
    console.log(`Connecting to WebSocket: ${url.toString()}`);

    this.ws.onopen = () => {
      console.log("WebSocket connected successfully");
      this.connectionState = 'connected';
      this.reconnectAttempts = 0; // Reset reconnect attempts on successful connection
      if (this.onConnect) this.onConnect();
    };

    this.ws.onmessage = (event) => {
      let message = JSON.parse(event.data);
      console.log("Received WS message:", message);

      if (message.type === "session_id") {
        this.session_id = message.content;
        console.log("Session ID received from server:", this.session_id);
        // Persist the session token in localStorage
        localStorage.setItem("sessionId", this.session_id);
      } else if (message.type === "ping") {
        this.ws.send(JSON.stringify({ type: "pong", token: this.session_id }));
        console.log("Sent pong with session token:", this.session_id);
      } else if (message.type === "error") {
        console.error("Server error:", message.content);
        if (message.content && message.content.includes("session")) {
          console.log("Session error detected, clearing session data");
          this.handleSessionError();
        }
      } else if (this.onMessage) {
        this.onMessage(message);
      }
    };

    this.ws.onclose = (event) => {
      console.log("WebSocket disconnected", event.code, event.reason);
      this.connectionState = 'disconnected';

      // Check if this was a clean close or an error
      if (event.code === 1000) {
        // Clean close, don't try to reconnect
        console.log("Clean WebSocket close, not attempting reconnection");
        if (this.onDisconnect) this.onDisconnect();
        return;
      }

      // Handle session-related close codes
      if (event.code === 1008 || event.reason?.includes("session")) {
        console.log("Session-related close detected");
        this.handleSessionError();
        return;
      }

      // Attempt reconnection for other close reasons
      this.attemptReconnection();
    };

    this.ws.onerror = (error) => {
      console.error("WebSocket error", error);
      this.connectionState = 'failed';
      if (this.onError) this.onError(error);
    };

    return true;
  }

  handleSessionError() {
    console.log("Handling session error - clearing session and starting fresh");
    this.clearSessionData();
    this.session_id = null;

    // Start a fresh connection without session ID
    setTimeout(() => {
      this.connectWebSocket(null);
    }, 1000);
  }

  attemptReconnection() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error("Max reconnection attempts reached, giving up");
      this.connectionState = 'failed';
      if (this.onDisconnect) this.onDisconnect();
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts - 1), 30000); // Exponential backoff, max 30s

    console.log(`Attempting reconnection ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`);

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectWebSocket();
    }, delay);
  }

  reconnectWebSocket() {
    console.log("Attempting to reconnect WebSocket");
    this.disconnect();

    if (this.session_id) {
      console.log("Reconnecting with existing session ID:", this.session_id);
      this.connectWebSocket(this.session_id);
    } else {
      console.log("Reconnecting with new session");
      this.connectWebSocket(null);
    }
  }

  sendMessage(message) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(
        JSON.stringify({
          type: "human",
          content: message,
        })
      );
      console.log("Sent message", message);
    } else {
      console.error("Cannot send message - WebSocket is not connected");
      // Try to reconnect if not already attempting
      if (this.connectionState !== 'connecting') {
        this.attemptReconnection();
      }
      if (this.onError) this.onError(new Error("WebSocket is not connected"));
    }
  }

  disconnect() {
    // Clear any pending reconnection
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      this.ws.close(1000, "Client disconnect"); // Clean close
      this.ws = null;
      console.log("WebSocket disconnected");
    }
    this.connectionState = 'disconnected';
  }

  closeWebSocketSession() {
    this.disconnect();
  }

  reset() {
    console.log("Resetting communication manager");
    this.disconnect();
    this.clearSessionData();
    this.reconnectAttempts = 0;
    this.session_id = null;

    // Start fresh connection
    setTimeout(() => {
      this.connectWebSocket(null);
    }, 500);
  }

  // Public method to get connection state
  getConnectionState() {
    return this.connectionState;
  }

  // Public method to manually trigger reconnection
  forceReconnect() {
    console.log("Force reconnecting...");
    this.reconnectAttempts = 0; // Reset attempts for manual reconnect
    this.disconnect();
    setTimeout(() => {
      this.connectWebSocket(this.session_id);
    }, 1000);
  }
}